/**
 * SkriptePage – Python-Programme starten, die auf dem eigenen Rechner laufen.
 *
 * Der Server führt hier nichts aus. Ein Klick legt nur einen Auftrag an; der
 * Runner auf dem jeweiligen Rechner holt ihn ab, startet das Programm dort und
 * schickt die Ausgabe zurück. Deshalb steht bei jedem Rechner eine Ampel: ist
 * er nicht erreichbar, wird gar nicht erst angeboten zu starten.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE } from "../api/config";

const API = `${API_BASE}/skripte/api`;

const STATUS_TEXT = {
  wartet: "wartet auf den Rechner",
  laeuft: "läuft gerade",
  ok: "erfolgreich",
  fehler: "fehlgeschlagen",
  abgebrochen: "abgebrochen",
  ausgefallen: "ausgefallen",
};

const STATUS_FARBE = {
  wartet: "var(--color-gray-500)",
  laeuft: "var(--color-info)",
  ok: "var(--color-success)",
  fehler: "var(--color-error)",
  abgebrochen: "var(--color-warning)",
  ausgefallen: "var(--color-warning)",
};

const S = {
  page: { maxWidth: 940, margin: "0 auto", padding: "24px 20px 60px", color: "var(--color-gray-800)" },
  h1: { fontSize: "1.5rem", margin: "0 0 4px" },
  sub: { color: "var(--color-gray-600)", margin: "0 0 20px", fontSize: "0.9rem", maxWidth: 640 },

  block: { background: "var(--bg-card)", border: "1px solid var(--color-gray-200)", borderRadius: 10, marginBottom: 16, overflow: "hidden" },
  blockKopf: { display: "flex", alignItems: "center", gap: 10, padding: "12px 18px", borderBottom: "1px solid var(--color-gray-200)" },
  punkt: (farbe) => ({ width: 10, height: 10, borderRadius: "50%", background: farbe, flexShrink: 0 }),
  rechnerName: { fontWeight: 600, fontSize: "1rem" },
  rechnerInfo: { fontSize: "0.8rem", color: "var(--color-gray-600)", marginLeft: "auto" },

  zeile: { display: "flex", alignItems: "flex-start", gap: 14, padding: "14px 18px", borderTop: "1px solid var(--color-gray-100)" },
  titel: { fontWeight: 600, fontSize: "0.95rem" },
  beschreibung: { fontSize: "0.85rem", color: "var(--color-gray-600)", marginTop: 3, lineHeight: 1.45 },
  hinweis: { fontSize: "0.8rem", color: "var(--color-warning)", marginTop: 5 },

  knopf: (aus) => ({
    padding: "7px 16px", borderRadius: 7, border: "none", fontSize: "0.88rem", fontWeight: 600,
    cursor: aus ? "not-allowed" : "pointer", opacity: aus ? 0.45 : 1,
    background: "var(--color-primary)", color: "#fff", whiteSpace: "nowrap",
  }),
  knopfKlein: {
    padding: "4px 10px", borderRadius: 6, border: "1px solid var(--color-gray-300)",
    background: "transparent", color: "var(--color-gray-700)", fontSize: "0.78rem", cursor: "pointer",
  },
  select: {
    padding: "5px 8px", borderRadius: 6, border: "1px solid var(--color-gray-300)",
    fontSize: "0.8rem", background: "var(--bg-card)", color: "var(--color-gray-800)",
  },
  planZeile: { display: "flex", alignItems: "center", gap: 8, marginTop: 9, flexWrap: "wrap", fontSize: "0.8rem", color: "var(--color-gray-600)" },

  pfeil: (aus) => ({
    width: 20, height: 16, padding: 0, lineHeight: "14px", fontSize: "0.7rem",
    border: "1px solid var(--color-gray-300)", borderRadius: 4, background: "transparent",
    color: "var(--color-gray-600)", cursor: aus ? "default" : "pointer", opacity: aus ? 0.3 : 1,
  }),
  stift: {
    border: "none", background: "transparent", cursor: "pointer", padding: "0 4px",
    color: "var(--color-gray-400)", fontSize: "0.85rem",
  },
  ordnerToggle: {
    border: "none", background: "transparent", cursor: "pointer", padding: 0,
    fontSize: "0.82rem", fontWeight: 600, color: "var(--color-gray-600)",
    textTransform: "uppercase", letterSpacing: "0.03em",
  },
  ordnerKasten: { padding: "12px 18px", borderTop: "1px solid var(--color-gray-100)", background: "var(--color-gray-50, transparent)" },
  ordnerTitel: { fontSize: "0.76rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.03em", color: "var(--color-gray-600)", marginBottom: 8 },
  ordnerZeile: { display: "flex", alignItems: "flex-start", gap: 10, padding: "6px 0" },
  ordnerPfad: { fontSize: "0.84rem", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", wordBreak: "break-all" },
  ordnerStand: { fontSize: "0.78rem", color: "var(--color-gray-600)", marginTop: 2 },

  logKasten: {
    background: "#0f172a", color: "#e2e8f0", borderRadius: 8, padding: "12px 14px",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: "0.78rem",
    whiteSpace: "pre-wrap", maxHeight: 340, overflowY: "auto", marginTop: 10, lineHeight: 1.5,
  },
  tabelle: { width: "100%", borderCollapse: "collapse", fontSize: "0.84rem" },
  th: { textAlign: "left", padding: "8px 10px", color: "var(--color-gray-600)", fontWeight: 600, borderBottom: "1px solid var(--color-gray-200)", fontSize: "0.76rem", textTransform: "uppercase", letterSpacing: "0.03em" },
  td: { padding: "8px 10px", borderBottom: "1px solid var(--color-gray-100)" },
  leer: { padding: "30px 18px", textAlign: "center", color: "var(--color-gray-500)", fontSize: "0.9rem" },
};

function zeitText(wert) {
  if (!wert) return "—";
  const d = new Date(wert);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function seitText(wert) {
  if (!wert) return "noch nie";
  const sek = Math.floor((Date.now() - new Date(wert).getTime()) / 1000);
  if (sek < 90) return "gerade eben";
  if (sek < 3600) return `vor ${Math.floor(sek / 60)} Minuten`;
  if (sek < 86400) return `vor ${Math.floor(sek / 3600)} Stunden`;
  return `vor ${Math.floor(sek / 86400)} Tagen`;
}

function planText(s) {
  if (!s.plan_aktiv) return "kein Zeitplan";
  if (s.muster === "intervall") return `alle ${s.intervall_minuten} Minuten`;
  if (s.muster === "wochentags") return `Mo–Fr um ${s.uhrzeit} Uhr`;
  return `täglich um ${s.uhrzeit} Uhr`;
}

export default function SkriptePage() {
  const [runner, setRunner] = useState([]);
  const [laeufe, setLaeufe] = useState([]);
  const [offenerLauf, setOffenerLauf] = useState(null);
  const [meldung, setMeldung] = useState("");
  const [geladen, setGeladen] = useState(false);
  const [paketName, setPaketName] = useState("");
  const [paketLaeuft, setPaketLaeuft] = useState(false);
  // Der Download bleibt eingeklappt, solange schon ein Rechner bekannt ist.
  // Wer längst einen Runner hat, braucht ihn nicht als Erstes zu sehen.
  const [paketOffen, setPaketOffen] = useState(false);
  const [ordnerEingabe, setOrdnerEingabe] = useState({});
  // Welcher Rechner „dieser hier" ist, merkt sich der Browser (also je PC).
  // Ein Browser kann den eigenen Rechnernamen nicht auslesen — deshalb einmal
  // wählen, danach zeigt die Seite nur die Programme DIESES Rechners. So sieht
  // Ingvar nicht die Programme von Lennards Rechner.
  const [dieserRechner, setDieserRechner] = useState(
    () => { try { return localStorage.getItem("skripteRechner") || ""; } catch { return ""; } });
  const [ordnerOffen, setOrdnerOffen] = useState(false);   // Ordner: eingeklappt
  const [bearbeiteName, setBearbeiteName] = useState(null); // katalog_id beim Umbenennen
  const [nameEingabe, setNameEingabe] = useState("");

  // Das Paket kommt als ZIP zurück, nicht als JSON — deshalb kein api()-Helfer,
  // sondern der Umweg über einen Blob, damit der Browser den Speichern-Dialog
  // zeigt statt die Datei anzuzeigen.
  const paketLaden = async () => {
    const name = paketName.trim();
    if (!name) return;
    setPaketLaeuft(true);
    setMeldung("");
    try {
      // Anmeldung mitschicken: Das Paket enthält einen gültigen Schlüssel und
      // ist deshalb als einziger Aufruf dieser Seite nur angemeldet erreichbar.
      const ausweis = localStorage.getItem("maToken") || "";
      const antwort = await fetch(
        `${API}/runner/paket?rechner=${encodeURIComponent(name)}`,
        { headers: ausweis ? { "x-ma-token": ausweis } : {} });
      if (antwort.status === 401 || antwort.status === 403) {
        throw new Error("nicht angemeldet — bitte neu anmelden und erneut versuchen");
      }
      if (!antwort.ok) throw new Error(`Server antwortete mit ${antwort.status}`);
      const daten = await antwort.blob();
      const url = URL.createObjectURL(daten);
      const a = document.createElement("a");
      a.href = url;
      a.download = `imho-runner-${name.replace(/[^A-Za-z0-9_.-]/g, "-")}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setPaketName("");
      laden();
    } catch (e) {
      setMeldung(`Paket konnte nicht erstellt werden: ${e.message}`);
    } finally {
      setPaketLaeuft(false);
    }
  };
  const logRef = useRef(null);

  const laden = useCallback(() => {
    fetch(`${API}/liste`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (j?.success) setRunner(j.runner || []); })
      .catch(() => {})
      .finally(() => setGeladen(true));
    fetch(`${API}/laeufe?limit=15`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (j?.success) setLaeufe(j.laeufe || []); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    laden();
    const t = setInterval(laden, 15000);
    return () => clearInterval(t);
  }, [laden]);

  // Während ein Lauf offen ist, das Protokoll mitlesen
  useEffect(() => {
    if (!offenerLauf?.id) return undefined;
    let aktiv = true;
    const holen = () => {
      fetch(`${API}/lauf/${offenerLauf.id}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((j) => {
          if (!aktiv || !j?.success) return;
          setOffenerLauf(j.lauf);
          if (["ok", "fehler", "abgebrochen"].includes(j.lauf.status)) laden();
        })
        .catch(() => {});
    };
    holen();
    const t = setInterval(holen, 2000);
    return () => { aktiv = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offenerLauf?.id]);

  // Protokoll immer am Ende halten
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [offenerLauf?.log]);

  const starten = (skript) => {
    setMeldung("");
    fetch(`${API}/start`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ katalog_id: skript.id }),
    })
      .then((r) => r.json())
      .then((j) => {
        if (j?.success) setOffenerLauf({ id: j.lauf_id, titel: skript.titel, status: "wartet", log: "" });
        else setMeldung(j?.error || "Start nicht möglich");
      })
      .catch(() => setMeldung("Der Server war nicht erreichbar."));
  };

  const planSetzen = (skript, wert) => {
    const [muster, param] = wert.split(":");
    const daten = {
      katalog_id: skript.id,
      aktiv: muster !== "aus",
      muster: muster === "aus" ? "taeglich" : muster,
      uhrzeit: muster === "intervall" ? null : param || "07:00",
      intervall_minuten: muster === "intervall" ? Number(param) : null,
    };
    fetch(`${API}/zeitplan`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(daten),
    }).then(() => laden()).catch(() => {});
  };

  const planWert = (s) => {
    if (!s.plan_aktiv) return "aus";
    if (s.muster === "intervall") return `intervall:${s.intervall_minuten}`;
    return `${s.muster}:${s.uhrzeit}`;
  };

  // Ordner ändern heißt festlegen, aus welchem Verzeichnis ein fremder Rechner
  // Programme ausführt — deshalb nur angemeldet, wie beim Runner-Paket.
  const mitAusweis = (pfad, optionen = {}) => {
    const ausweis = localStorage.getItem("maToken") || "";
    return fetch(`${API}${pfad}`, {
      ...optionen,
      headers: {
        ...(optionen.body ? { "Content-Type": "application/json" } : {}),
        ...(ausweis ? { "x-ma-token": ausweis } : {}),
      },
    }).then(async (r) => {
      if (r.status === 401 || r.status === 403) {
        throw new Error("nicht angemeldet — bitte neu anmelden");
      }
      if (!r.ok) throw new Error(`Server antwortete mit ${r.status}`);
      return r.json();
    });
  };

  const ordnerHinzufuegen = (rechner) => {
    const pfad = (ordnerEingabe[rechner.id] || "").trim();
    if (!pfad) return;
    setMeldung("");
    mitAusweis("/ordner", {
      method: "POST",
      body: JSON.stringify({ runner_id: rechner.id, pfad }),
    })
      .then(() => {
        setOrdnerEingabe((v) => ({ ...v, [rechner.id]: "" }));
        laden();
      })
      .catch((e) => setMeldung(`Ordner konnte nicht hinzugefügt werden: ${e.message}`));
  };

  const ordnerUmschalten = (ordner) => {
    setMeldung("");
    mitAusweis("/ordner/aendern", {
      method: "POST",
      body: JSON.stringify({ ordner_id: ordner.id, aktiv: !ordner.aktiv }),
    })
      .then(laden)
      .catch((e) => setMeldung(`Ordner konnte nicht geändert werden: ${e.message}`));
  };

  const ordnerEntfernen = (ordner) => {
    // Mit dem Ordner verschwinden auch die Programme, die nur aus ihm kamen —
    // das gehört gesagt, bevor jemand klickt.
    if (!window.confirm(
      `Ordner aus der Liste nehmen?\n\n${ordner.pfad}\n\n`
      + "Die Programme daraus verschwinden aus dem Menü. Auf dem Rechner "
      + "selbst wird nichts gelöscht.")) return;
    setMeldung("");
    mitAusweis(`/ordner/${ordner.id}`, { method: "DELETE" })
      .then(laden)
      .catch((e) => setMeldung(`Ordner konnte nicht entfernt werden: ${e.message}`));
  };

  const rechnerEntfernen = (rechner) => {
    if (!window.confirm(
      `Rechner „${rechner.name}“ aus der Liste nehmen?\n\n`
      + "Sinnvoll, nachdem der Runner auf dem Rechner deinstalliert wurde. "
      + "Läuft er dort noch, meldet er sich danach vergeblich.")) return;
    setMeldung("");
    mitAusweis(`/runner/${rechner.id}`, { method: "DELETE" })
      .then(laden)
      .catch((e) => setMeldung(`Rechner konnte nicht entfernt werden: ${e.message}`));
  };

  // Ein Pfad unterhalb der Dropbox liegt auf allen Rechnern gleich; alles
  // andere gibt es nur hier. Reine Anzeige — erlaubt sind beide.
  const istGeteilt = (pfad) => /dropbox/i.test(pfad || "");
  const nameSchonVergeben = runner.some(
    (r) => r.name.toLowerCase() === paketName.trim().toLowerCase());

  // Programm umbenennen — Anzeigename getrennt vom gemeldeten Namen.
  const nameSpeichern = (skript) => {
    const neu = nameEingabe.trim();
    setBearbeiteName(null);
    mitAusweis("/katalog/umbenennen", {
      method: "POST",
      body: JSON.stringify({ katalog_id: skript.id, anzeigename: neu }),
    }).then(laden).catch((e) => setMeldung(`Umbenennen ging nicht: ${e.message}`));
  };

  // Programm in der Liste nach oben/unten schieben. Wir schicken die komplette
  // neue Reihenfolge der Katalog-IDs; das Backend vergibt 10, 20, 30 …
  const verschieben = (liste, index, richtung) => {
    const ziel = index + richtung;
    if (ziel < 0 || ziel >= liste.length) return;
    const ids = liste.map((s) => s.id);
    [ids[index], ids[ziel]] = [ids[ziel], ids[index]];
    mitAusweis("/katalog/reihenfolge", {
      method: "POST", body: JSON.stringify({ reihenfolge: ids }),
    }).then(laden).catch((e) => setMeldung(`Reihenfolge ging nicht: ${e.message}`));
  };

  // Den zuletzt gewählten Rechner merken, sobald die Liste da ist. Ist keiner
  // gewählt (oder der gewählte weg), den einzigen erreichbaren nehmen.
  useEffect(() => {
    if (!runner.length) return;
    const gibtsNoch = runner.some((r) => String(r.id) === String(dieserRechner));
    if (dieserRechner && gibtsNoch) return;
    const online = runner.filter((r) => r.online);
    const wahl = (online.length === 1 ? online[0] : runner[0]);
    if (wahl) {
      setDieserRechner(String(wahl.id));
      try { localStorage.setItem("skripteRechner", String(wahl.id)); } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runner]);

  const rechnerWaehlen = (id) => {
    setDieserRechner(String(id));
    try { localStorage.setItem("skripteRechner", String(id)); } catch {}
  };

  const aktiverRechner = runner.find((r) => String(r.id) === String(dieserRechner));

  return (
    <div style={S.page}>
      <h1 style={S.h1}>Programme</h1>
      <p style={S.sub}>
        Diese Programme laufen nicht auf dem Server, sondern auf dem jeweils
        angegebenen Rechner. Ein Klick legt den Auftrag an — der Rechner holt ihn
        ab und schickt das Protokoll zurück.
      </p>

      {meldung && (
        <div style={{ ...S.block, padding: "12px 18px", color: "var(--color-error)", fontSize: "0.9rem" }}>
          {meldung}
        </div>
      )}

      {geladen && runner.length === 0 && (
        <div style={S.block}>
          <div style={S.leer}>
            Noch kein Rechner angemeldet.<br />
            Damit ein Rechner Programme ausführen kann, braucht er einmalig das
            Runner-Paket — unten herunterladen.
          </div>
        </div>
      )}

      {/* Rechner-Auswahl: nur nötig, wenn es mehr als einen gibt. Der Browser
          kann seinen eigenen Rechner nicht erkennen, deshalb einmal wählen —
          die Wahl bleibt in DIESEM Browser (also auf diesem PC) gespeichert. */}
      {runner.length > 1 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 0 14px", flexWrap: "wrap" }}>
          <span style={{ fontSize: "0.85rem", color: "var(--color-gray-600)" }}>Dieser Rechner:</span>
          {runner.map((r) => (
            <button key={r.id}
              onClick={() => rechnerWaehlen(r.id)}
              style={{
                display: "flex", alignItems: "center", gap: 6, padding: "5px 12px",
                borderRadius: 999, fontSize: "0.85rem", cursor: "pointer",
                border: `1px solid ${String(r.id) === String(dieserRechner) ? "var(--color-primary)" : "var(--color-gray-300)"}`,
                background: String(r.id) === String(dieserRechner) ? "var(--color-primary)" : "transparent",
                color: String(r.id) === String(dieserRechner) ? "#fff" : "var(--color-gray-700)",
                fontWeight: String(r.id) === String(dieserRechner) ? 600 : 400,
              }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%",
                background: r.online ? "var(--color-success)" : "var(--color-gray-400)" }} />
              {r.name}
            </button>
          ))}
        </div>
      )}

      {aktiverRechner && (() => {
        const r = aktiverRechner;
        const progs = r.skripte || [];
        return (
        <div style={S.block}>
          <div style={S.blockKopf}>
            <span style={S.punkt(r.online ? "var(--color-success)" : "var(--color-gray-400)")} />
            <span style={S.rechnerName}>{r.name}</span>
            <span style={S.rechnerInfo}>
              {r.online ? "erreichbar" : `zuletzt gesehen ${seitText(r.zuletzt_gesehen)}`}
              {r.betriebssystem ? ` · ${r.betriebssystem}` : ""}
            </span>
          </div>

          {/* Programme — die eigentliche Liste, minimal gehalten. */}
          {progs.length === 0 && (
            <div style={S.leer}>Dieser Rechner meldet zurzeit keine Programme.</div>
          )}
          {progs.map((s, i) => (
            <div key={s.id} style={S.zeile}>
              {/* Sortier-Pfeile */}
              <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 2 }}>
                <button style={S.pfeil(i === 0)} disabled={i === 0}
                  title="nach oben" onClick={() => verschieben(progs, i, -1)}>▲</button>
                <button style={S.pfeil(i === progs.length - 1)} disabled={i === progs.length - 1}
                  title="nach unten" onClick={() => verschieben(progs, i, +1)}>▼</button>
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                {bearbeiteName === s.id ? (
                  <input autoFocus value={nameEingabe}
                    onChange={(e) => setNameEingabe(e.target.value)}
                    onBlur={() => nameSpeichern(s)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") nameSpeichern(s);
                      if (e.key === "Escape") setBearbeiteName(null);
                    }}
                    placeholder={s.name || s.datei}
                    style={{
                      fontWeight: 600, fontSize: "0.95rem", padding: "3px 8px",
                      border: "1px solid var(--color-primary)", borderRadius: 6,
                      background: "var(--bg-card)", color: "var(--color-gray-800)", width: "100%", maxWidth: 320,
                    }} />
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={S.titel}>{s.titel}</span>
                    <button style={S.stift} title="Umbenennen"
                      onClick={() => { setBearbeiteName(s.id); setNameEingabe(s.anzeigename || ""); }}>
                      ✎
                    </button>
                  </div>
                )}
                {s.beschreibung && <div style={S.beschreibung}>{s.beschreibung}</div>}
                {s.braucht_browser && (
                  <div style={S.hinweis}>
                    ⚠ Öffnet auf {r.name} ein Browserfenster — dort anmelden.
                  </div>
                )}
                <div style={S.planZeile}>
                  <span>Zeitplan:</span>
                  <select style={S.select} value={planWert(s)}
                    onChange={(e) => planSetzen(s, e.target.value)}>
                    <option value="aus">aus — nur von Hand</option>
                    <option value="taeglich:07:00">täglich 07:00</option>
                    <option value="taeglich:12:00">täglich 12:00</option>
                    <option value="taeglich:18:00">täglich 18:00</option>
                    <option value="wochentags:07:00">Mo–Fr 07:00</option>
                    <option value="wochentags:18:00">Mo–Fr 18:00</option>
                    <option value="intervall:120">alle 2 Stunden</option>
                    <option value="intervall:360">alle 6 Stunden</option>
                  </select>
                  <span>{planText(s)}</span>
                </div>
              </div>
              <button style={S.knopf(!r.online)} disabled={!r.online}
                title={r.online ? "" : `${r.name} ist gerade nicht erreichbar`}
                onClick={() => starten(s)}>
                Starten
              </button>
            </div>
          ))}

          {/* Ordner — eingeklappt, weil man normalerweise nur die Programme
              sehen will. Für die Einrichtung aufklappbar. */}
          <div style={S.ordnerKasten}>
            <button style={S.ordnerToggle} onClick={() => setOrdnerOffen((v) => !v)}>
              {ordnerOffen ? "▾" : "▸"} Programm-Ordner verwalten
              <span style={{ color: "var(--color-gray-500)", fontWeight: 400 }}>
                {" "}({(r.ordner || []).length})
              </span>
            </button>
            {ordnerOffen && (
              <div style={{ marginTop: 10 }}>
                {(r.ordner || []).length === 0 && (
                  <div style={{ fontSize: "0.83rem", color: "var(--color-gray-500)" }}>
                    Noch keiner eingetragen — der Runner meldet beim nächsten Mal,
                    was in seiner Einrichtung steht.
                  </div>
                )}
                {(r.ordner || []).map((o) => (
                  <div key={o.id} style={S.ordnerZeile}>
                    <input type="checkbox" checked={!!o.aktiv} title="Ordner durchsuchen"
                      onChange={() => ordnerUmschalten(o)} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ ...S.ordnerPfad, opacity: o.aktiv ? 1 : 0.5 }}>{o.pfad}</div>
                      <div style={S.ordnerStand}>
                        {o.gefunden === 0 ? (
                          <span style={{ color: "var(--color-error)", fontWeight: 600 }}>
                            {o.meldung || "Ordner auf diesem Rechner nicht gefunden"}
                          </span>
                        ) : o.gefunden === 1 ? (
                          <span>
                            {o.anzahl_skripte === 1 ? "1 Programm" : `${o.anzahl_skripte || 0} Programme`}
                            {o.meldung ? ` · ${o.meldung}` : ""}
                          </span>
                        ) : (<span>noch nicht vom Rechner bestätigt</span>)}
                        {" · "}
                        {istGeteilt(o.pfad) ? "in der Dropbox — auf allen Rechnern gleich" : "nur auf diesem Rechner"}
                      </div>
                    </div>
                    <button style={S.knopfKlein} onClick={() => ordnerEntfernen(o)}>entfernen</button>
                  </div>
                ))}
                <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                  <input type="text" value={ordnerEingabe[r.id] || ""}
                    placeholder="Weiterer Ordner, z. B. /Users/name/Documents/Meine Skripte"
                    onChange={(e) => setOrdnerEingabe((v) => ({ ...v, [r.id]: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === "Enter") ordnerHinzufuegen(r); }}
                    style={{
                      flex: "1 1 260px", minWidth: 200, padding: "6px 10px", fontSize: "0.83rem",
                      border: "1px solid var(--color-gray-300)", borderRadius: 6,
                      background: "var(--bg-card)", color: "var(--color-gray-800)",
                    }} />
                  <button style={S.knopfKlein} onClick={() => ordnerHinzufuegen(r)}
                    disabled={!(ordnerEingabe[r.id] || "").trim()}>Ordner hinzufügen</button>
                </div>
                <div style={{ ...S.ordnerStand, marginTop: 8, display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                  <span>Der Rechner übernimmt Änderungen binnen einer Minute. Örtlich oder Dropbox — beides geht.</span>
                  <button style={S.knopfKlein} onClick={() => rechnerEntfernen(r)}
                    title="Nach der Deinstallation aus der Liste nehmen">Rechner entfernen</button>
                </div>
              </div>
            )}
          </div>
        </div>
        );
      })()}

      {/* Runner-Paket. Steht bewusst HINTER den Rechnern und eingeklappt,
          sobald es einen gibt: Wer schon einen Runner hat, braucht kein
          Angebot, das ihn nicht betrifft — und ein zweiter Download unter
          demselben Namen würde den laufenden Runner abhängen. */}
      <div style={S.block}>
        <div style={{ ...S.blockKopf, cursor: paketOffen ? "default" : "pointer" }}
          onClick={() => { if (!paketOffen) setPaketOffen(true); }}>
          <strong style={{ fontSize: "0.95rem" }}>
            {runner.length === 0 ? "Rechner einrichten" : "Weiteren Rechner einrichten"}
          </strong>
          {runner.length > 0 && (
            <button style={{ ...S.knopfKlein, marginLeft: "auto" }}
              onClick={(e) => { e.stopPropagation(); setPaketOffen(!paketOffen); }}>
              {paketOffen ? "zuklappen" : "aufklappen"}
            </button>
          )}
        </div>
        {(paketOffen || runner.length === 0) && (
          <div style={{ padding: "14px 18px" }}>
            <p style={{ margin: "0 0 12px", fontSize: "0.9rem", color: "var(--color-text-secondary)" }}>
              Lädt ein fertiges Paket für Mac und Windows. Auf dem Zielrechner
              genügt ein Doppelklick — Python-Umgebung, alle Bausteine und der
              Browser für die DATEV-Programme werden dabei selbst installiert.
              Der Zugangsschlüssel steckt bereits im Paket, und die
              Programm-Ordner werden beim Einrichten gesucht statt abgetippt.
            </p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <input
                type="text" value={paketName} placeholder="Name des Rechners, z. B. IHY-MacBook"
                onChange={(e) => setPaketName(e.target.value)}
                style={{
                  flex: "1 1 240px", minWidth: 200, padding: "9px 12px",
                  border: `1px solid ${nameSchonVergeben ? "var(--color-warning)" : "var(--color-border)"}`,
                  borderRadius: 8,
                  background: "var(--color-bg)", color: "var(--color-text)",
                }}
              />
              <button onClick={paketLaden} disabled={paketLaeuft || !paketName.trim()}
                style={{
                  padding: "9px 18px", border: "none", borderRadius: 8,
                  background: nameSchonVergeben ? "var(--color-warning)" : "var(--color-primary)",
                  color: "#fff", fontWeight: 600,
                  cursor: paketLaeuft || !paketName.trim() ? "not-allowed" : "pointer",
                  opacity: paketLaeuft || !paketName.trim() ? 0.55 : 1,
                }}>
                {paketLaeuft ? "wird erstellt …" : "Runner herunterladen"}
              </button>
            </div>
            {/* Diese Folge kennt der Server längst — die Oberfläche verschwieg
                sie bisher, bis es zu spät war. */}
            {nameSchonVergeben ? (
              <p style={{ margin: "10px 0 0", fontSize: "0.85rem", color: "var(--color-warning)", fontWeight: 600 }}>
                Diesen Rechner gibt es schon. Beim Herunterladen bekommt er einen
                neuen Schlüssel — der bisherige gilt dann nicht mehr, und ein dort
                laufender Runner meldet sich ab. Für einen zweiten Rechner bitte
                einen anderen Namen wählen.
              </p>
            ) : (
              <p style={{ margin: "10px 0 0", fontSize: "0.82rem", color: "var(--color-text-secondary)" }}>
                Jeder Rechner braucht einen eigenen Namen.
              </p>
            )}
          </div>
        )}
      </div>

      {offenerLauf && (
        <div style={S.block}>
          <div style={S.blockKopf}>
            <span style={S.punkt(STATUS_FARBE[offenerLauf.status] || "var(--color-gray-400)")} />
            <span style={S.rechnerName}>{offenerLauf.titel}</span>
            <span style={S.rechnerInfo}>
              {STATUS_TEXT[offenerLauf.status] || offenerLauf.status}
              {offenerLauf.exitcode != null ? ` · Code ${offenerLauf.exitcode}` : ""}
            </span>
          </div>
          <div style={{ padding: "0 18px 16px" }}>
            <div ref={logRef} style={S.logKasten}>
              {offenerLauf.log || "Warte auf den Rechner …"}
            </div>
            <button style={{ ...S.knopfKlein, marginTop: 10 }} onClick={() => setOffenerLauf(null)}>
              Protokoll schließen
            </button>
          </div>
        </div>
      )}

      <div style={S.block}>
        <div style={S.blockKopf}>
          <span style={S.rechnerName}>Zuletzt gelaufen</span>
        </div>
        {laeufe.length === 0 ? (
          <div style={S.leer}>Noch nichts gelaufen.</div>
        ) : (
          <table style={S.tabelle}>
            <thead>
              <tr>
                <th style={S.th}>Programm</th>
                <th style={S.th}>Rechner</th>
                <th style={S.th}>Wann</th>
                <th style={S.th}>Ausgelöst</th>
                <th style={S.th}>Ergebnis</th>
              </tr>
            </thead>
            <tbody>
              {laeufe.map((l) => (
                <tr key={l.id}>
                  <td style={S.td}>
                    <button
                      style={{ ...S.knopfKlein, border: "none", padding: 0, color: "var(--color-primary)" }}
                      onClick={() => setOffenerLauf({ id: l.id, titel: l.titel, status: l.status, log: "" })}
                    >
                      {l.titel}
                    </button>
                  </td>
                  <td style={S.td}>{l.runner_name || "—"}</td>
                  <td style={S.td}>{zeitText(l.angelegt_am)}</td>
                  <td style={S.td}>{l.gestartet_von === "plan" ? "nach Plan" : "von Hand"}</td>
                  <td style={{ ...S.td, color: STATUS_FARBE[l.status] }}>
                    {STATUS_TEXT[l.status] || l.status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
